import type { Web3TxMultiCalcSWR } from "@/hooks/tx-flow/useDepositCalculate";
import type { ResponseTxFees } from "@/hooks/tx-flow/useDepositStart";
import type { SupWeb3DepositStep, SupWeb3WithdrawSingleVault, SupWithdrawableBalance, SupWithdrawableToken } from "@/types";
import React, { type Dispatch, type SetStateAction, useMemo, useState } from "react";

import { useDynamicContext, useIsLoggedIn } from "@dynamic-labs/sdk-react-core";
import { SupButton, SupText } from "@repo/core-ui";
import { useStoredData } from "@/store";
import { useRouteState } from "@/components/Web3/Common/Route/State";

import Web3CommonHighFeesWarning from "@/components/Web3/Common/HighFeesWarning";
import Web3TxSettingsSlippageWarning from "@/components/Web3/TxSettings/SlippageWarning";
import useGetRefundAddress from "@/hooks/web3/useGetRefundAddress";
import SupUiLoading from "@/components/Ui/Loading";
import Web3TransmuteWithdrawTrigger from "@/components/Web3/Transmute/WithdrawTrigger";
import Web3CommonRefundAddress from "@/components/Web3/Common/RefundAddress";
import Web3WithdrawTransfer from "@/components/Web3/Withdraw/Transfer";
import Web3WithdrawApproval from "@/components/Web3/Withdraw/Approval";
import Web3CommonSwitchNetwork from "@/components/Web3/Common/SwitchNetwork";
import Web3CommonAuthenticate from "@/components/Web3/Common/Authenticate";
import Web3CommonSummary from "@/components/Web3/Common/Summary";

interface SupWeb3StepConfirmationProps {
  vaultList: SupWeb3WithdrawSingleVault[];
  step: SupWeb3DepositStep;
  selectedBalance?: SupWithdrawableBalance | SupWithdrawableToken;
  setTransferTx: Dispatch<SetStateAction<string | undefined>>;
  setStep: Dispatch<SetStateAction<SupWeb3DepositStep>>;
  targetChainId: number;
  totalGasCost?: number;
  setTotalGasCost: Dispatch<SetStateAction<number | undefined>>;
  setPauseRequests: Dispatch<SetStateAction<boolean>>;
  simulate: boolean;
  setSimulate: Dispatch<SetStateAction<boolean>>;
  calculatedRoutes: Web3TxMultiCalcSWR;
}

export default function Web3WithdrawConfirmation({
  targetChainId,
  vaultList,
  setTransferTx,
  selectedBalance,
  step,
  setStep,
  totalGasCost,
  setTotalGasCost,
  setPauseRequests,
  simulate,
  setSimulate,
  calculatedRoutes,
}: SupWeb3StepConfirmationProps) {
  const { network } = useDynamicContext();
  const { selectedRoutes } = useRouteState();

  const storedData = useStoredData();

  const isConnected = useIsLoggedIn();

  const [isApprovalRequired, setIsApprovalRequired] = useState<boolean>(true);

  const isRoutesReady = useMemo(() => {
    return calculatedRoutes.data !== undefined && calculatedRoutes.data.find(singleRoute => !singleRoute.liq.ok) === undefined;
  }, [calculatedRoutes.data]);

  const isLoading = useMemo(() => {
    return step === "Default" ? false : calculatedRoutes.isLoading;
  }, [calculatedRoutes.isLoading, step]);

  const [discountedAmount, setDiscountedAmount] = useState<ResponseTxFees | undefined>();

  const isAmountsReady = useMemo(() => {
    const filteredAmountVaults = vaultList.filter(singleVault => !!singleVault.amount);
    return filteredAmountVaults.length === vaultList.length;
  }, [vaultList]);

  const { address: refundAddress, isLoading: isLoadingRefundAddressCheck } = useGetRefundAddress(targetChainId);

  const requireRefundAddress = useMemo(() => {
    return !refundAddress;
  }, [refundAddress]);

  const requireSwitchChain = useMemo(
    () => (network && selectedBalance ? parseInt(`${network}`) !== parseInt(`${targetChainId}`) : false),
    [network, selectedBalance, targetChainId]
  );

  const combinedLoading = useMemo(() => isLoading || isLoadingRefundAddressCheck, [isLoading, isLoadingRefundAddressCheck]);

  const requireTransmute = useMemo(() => {
    const hasSuperPoolRoute = selectedRoutes.some(single => typeof single.selectedRoute === "object" && single.selectedRoute?.isERC20);
    const singleVaultPosition = storedData.withdrawCart.find(single => !single.is_erc20);
    return hasSuperPoolRoute && singleVaultPosition;
  }, [selectedRoutes, storedData.withdrawCart]);

  return (
    <div className="-mx-6 space-y-2 px-6">
      {step === "Calculate" && isAmountsReady && (
        <div className="inline-flex h-2 w-full flex-col items-center justify-center">
          <div className="h-0 self-stretch border-t-0.5 border-neutral-600" />
        </div>
      )}

      {step === "Calculate" && isAmountsReady && !requireRefundAddress && (
        <Web3CommonSummary
          selectedRoutes={selectedRoutes}
          type="withdraw"
          isLoading={isLoading}
          totalGasCost={totalGasCost}
          vaultList={vaultList}
          discountedAmount={discountedAmount}
        />
      )}

      {requireRefundAddress && !combinedLoading && (
        <div className="bg-warning-50 p-4">
          <SupText variant="subtitle1" className="text-warning-900">
            Non-create2 wallet is being used. Please set a refund address. You may not be able to claim vault rewards.
          </SupText>
        </div>
      )}

      {selectedBalance && !combinedLoading && requireRefundAddress && <Web3CommonRefundAddress />}

      {combinedLoading ? (
        <div className="mt-4 flex h-[40px] w-full flex-col items-center justify-between space-x-[4px]">
          <SupUiLoading />
        </div>
      ) : (
        <>
          {selectedBalance && step !== "Default"
            ? isRoutesReady && (
                <>
                  {/* High-slippage warning */}
                  <Web3TxSettingsSlippageWarning />

                  {/* High fees warning */}
                  <Web3CommonHighFeesWarning totalGasCost={totalGasCost} discountedAmount={discountedAmount} />

                  {step === "Calculate" && !isConnected && <Web3CommonAuthenticate connectOnly />}

                  {requireSwitchChain && isConnected && <Web3CommonSwitchNetwork targetChainId={targetChainId} />}

                  {step === "Calculate" && isConnected && !requireSwitchChain && isAmountsReady && (
                    <>
                      {requireTransmute ? (
                        <Web3TransmuteWithdrawTrigger selectedBalance={selectedBalance} vaultList={vaultList} />
                      ) : isApprovalRequired ? (
                        <Web3WithdrawApproval
                          calculatedRoutes={calculatedRoutes}
                          vaultList={vaultList}
                          setIsApprovalRequired={setIsApprovalRequired}
                          setPauseRequests={setPauseRequests}
                          selectedRoutes={selectedRoutes}
                        />
                      ) : (
                        <Web3WithdrawTransfer
                          calculatedRoutes={calculatedRoutes}
                          simulate={simulate}
                          setSimulate={setSimulate}
                          targetChainId={targetChainId}
                          selectedBalance={selectedBalance}
                          vaultList={vaultList}
                          setStep={setStep}
                          setTransferTx={setTransferTx}
                          setTotalGasCost={setTotalGasCost}
                          setPauseRequests={setPauseRequests}
                          setDiscountedAmount={setDiscountedAmount}
                        />
                      )}
                    </>
                  )}
                </>
              )
            : step === "Default" &&
              (!isConnected ? (
                <Web3CommonAuthenticate connectOnly />
              ) : (
                <SupButton type="button" fluid onClick={() => setStep("Balance")} disabled={vaultList.length === 0}>
                  SELECT YOUR TARGET TOKEN
                </SupButton>
              ))}
        </>
      )}
    </div>
  );
}
